import { useState, type ReactNode } from 'react';
import { useLocation } from '@tanstack/react-router';
import { AlertTriangle, X } from 'lucide-react';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { MobileSidebar } from './MobileSidebar';
import { Button } from '@/components/ui/button';
import { impersonation } from '@/lib/api-client';
import { useStopImpersonation } from '@/features/admin/hooks';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { useCrmEventStream } from '@/features/crm/shared/hooks/useCrmEventStream';

interface DashboardLayoutProps {
  children: ReactNode;
}

/**
 * Dashboard shell — sidebar, header, main content area, impersonation
 * banner. Route protection is the caller's responsibility (see the
 * `withProtection*` helpers in `router.tsx`), while optional shell integrations
 * remain permission-gated so denied routes cannot start protected data flows.
 */
export function DashboardLayout({ children }: DashboardLayoutProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isImpersonating = impersonation.isImpersonating();
  const location = useLocation();
  const isCrmRoute = location.pathname.startsWith('/crm');
  const { hasPermission } = usePermissions();
  const canUseCrmEventStream = isCrmRoute && hasPermission('crm:sse:read:tenant');
  const stopImpersonation = useStopImpersonation();

  useCrmEventStream(canUseCrmEventStream);

  const toggleSidebar = () => setSidebarCollapsed(!sidebarCollapsed);
  const toggleMobileMenu = () => setMobileMenuOpen(!mobileMenuOpen);
  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      {isImpersonating && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-yellow-500 text-yellow-900 px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            <span className="text-sm font-medium">
              You are currently impersonating a tenant. Actions will be performed as this tenant.
            </span>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => stopImpersonation.mutate()}
            disabled={stopImpersonation.isPending}
            className="bg-yellow-600 border-yellow-600 text-white hover:bg-yellow-700 hover:text-white"
          >
            <X className="h-4 w-4 mr-1" />
            Exit impersonation
          </Button>
        </div>
      )}

      <Sidebar collapsed={sidebarCollapsed} onToggle={toggleSidebar} />
      <MobileSidebar isOpen={mobileMenuOpen} onClose={closeMobileMenu} />

      <div
        className={`transition-all duration-300 pl-0 ${sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64'}`}
      >
        <Header sidebarCollapsed={sidebarCollapsed} onMenuClick={toggleMobileMenu} />
        <main
          className={`min-h-[calc(100vh-4rem)] px-4 md:px-6 lg:px-8 pb-4 md:pb-6 lg:pb-8 pt-20 ${
            isImpersonating ? 'pt-28' : ''
          }`}
        >
          {children}
        </main>
      </div>
    </div>
  );
}
